// Shared card for an email_document_section — preview on top, footer with
// name + category chip + slot count below. Used by the in-editor section
// library panel (click = insert) and the admin sections list (click = edit,
// with an actions overlay for delete). Same visual language in both places
// so authors see exactly what the library will serve.

import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { AutoAwesome as SlotIcon } from '@mui/icons-material';

import { SectionCardPreview } from './SectionCardPreview';
import { SectionRawHtmlCardPreview } from './SectionRawHtmlCardPreview';
import type { BrandResolverContext, SlotDefault, SlotSchema } from './merge-tag-resolver';
import { mediaOffsetForId } from './resolver';
import type { EmailNode } from '../types';
import { BORDER, INDIGO, INDIGO_BG, TEXT, TEXT_DIM } from '../constants';

export interface SectionLibraryCardSectionData {
  id: string;
  name: string;
  category: string;
  // Optional — raw HTML / MJML rows have no structured node tree. The card
  // renders the rawHtml in a scaled iframe instead when absent.
  node?: unknown;
  slots?: unknown;
  slotDefaults?: unknown;
  // Raw HTML body for sourceFormat='html' rows. Rendered as a scaled
  // iframe thumbnail when present and `node` is absent.
  rawHtml?: string | null;
  // Raw MJML body for sourceFormat='mjml' rows. Compiled client-side via
  // mjml-browser into the same iframe thumbnail.
  rawMjml?: string | null;
  thumbnailUrl?: string | null;
}

export interface SectionLibraryCardProps {
  section: SectionLibraryCardSectionData;
  brand?: BrandResolverContext;
  pickRecentMedia?: (slotName: string, used: Set<string>, offset?: number) => string | undefined;
  // Cutout-aware picker for product-cutout slots; falls through to
  // pickRecentMedia when absent.
  pickTransparentMedia?: (slotName: string, used: Set<string>, offset?: number) => string | undefined;
  pickProduct?: (slotName: string, used: Set<string>) => string | undefined;
  // Multi-output product picker for dotted slot refs.
  pickProductRich?: (
    slotName: string,
    used: Set<string>,
  ) => import('@nexmail/shared-util/email-builder').PickedProduct | undefined;
  // Rotation seed into the recent-media pool. Optional — caller usually
  // lets the card derive it from section.id via mediaOffsetForId.
  mediaOffset?: number;
  // Width of the rendered card — drives the preview's internal scale factor.
  // Defaults to 308 to match the in-editor floating panel.
  displayWidth?: number;
  maxDisplayHeight?: number;
  minDisplayHeight?: number;
  onClick?: () => void;
  // Optional overlay rendered top-right on the preview (e.g. a delete icon).
  // Shown on hover only. Stop propagation inside your handlers if the parent
  // onClick should not fire.
  actions?: ReactNode;
  // Dim the card — used for inactive sections in admin.
  dim?: boolean;
}

export function SectionLibraryCard({
  section,
  brand,
  pickRecentMedia,
  pickTransparentMedia,
  pickProduct,
  pickProductRich,
  mediaOffset,
  displayWidth = 308,
  maxDisplayHeight = 260,
  minDisplayHeight = 140,
  onClick,
  actions,
  dim = false,
}: SectionLibraryCardProps) {
  const slotCount = section.slots ? Object.keys(section.slots as Record<string, unknown>).length : 0;

  return (
    <Box
      onClick={onClick}
      sx={{
        width: displayWidth,
        border: `1px solid ${BORDER}`,
        borderRadius: 1,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.12s',
        opacity: dim ? 0.5 : 1,
        bgcolor: '#FFFFFF',
        position: 'relative',
        '&:hover': onClick
          ? {
              borderColor: INDIGO,
              boxShadow: 2,
              bgcolor: INDIGO_BG,
              '& .section-card-actions': { opacity: 1 },
            }
          : undefined,
      }}
    >
      <Box
        sx={{
          bgcolor: 'grey.50',
          borderBottom: `1px solid ${BORDER}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {section.thumbnailUrl ? (
          <Box
            sx={{
              width: displayWidth,
              height: Math.min(maxDisplayHeight, 220),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#FFFFFF',
            }}
          >
            <Box
              component="img"
              src={section.thumbnailUrl}
              alt={section.name}
              sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
            />
          </Box>
        ) : section.node ? (
          <SectionCardPreview
            node={section.node as EmailNode}
            slots={(section.slots ?? {}) as Record<string, SlotSchema>}
            slotDefaults={(section.slotDefaults ?? {}) as Record<string, SlotDefault>}
            brand={brand}
            pickRecentMedia={pickRecentMedia}
            pickTransparentMedia={pickTransparentMedia}
            pickProduct={pickProduct}
            pickProductRich={pickProductRich}
            mediaOffset={mediaOffset ?? mediaOffsetForId(section.id)}
            displayWidth={displayWidth}
            maxDisplayHeight={maxDisplayHeight}
            minDisplayHeight={minDisplayHeight}
          />
        ) : section.rawHtml || section.rawMjml ? (
          <SectionRawHtmlCardPreview
            rawHtml={(section.rawHtml ?? section.rawMjml) as string}
            isMjml={!section.rawHtml}
            brand={brand}
            displayWidth={displayWidth}
            maxDisplayHeight={maxDisplayHeight}
            minDisplayHeight={minDisplayHeight}
          />
        ) : (
          <Box
            sx={{
              width: displayWidth,
              height: Math.min(maxDisplayHeight, 140),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#FAFAFA',
              fontSize: 11,
              color: TEXT_DIM,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Empty
          </Box>
        )}

        {onClick ? (
          <Box
            className="section-card-actions"
            sx={{
              position: 'absolute',
              bottom: 6,
              right: 6,
              px: 1,
              py: 0.25,
              borderRadius: 99,
              bgcolor: INDIGO,
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.02em',
              opacity: 0,
              transition: 'opacity 0.12s',
              pointerEvents: 'none',
              boxShadow: 1,
            }}
          >
            + Add
          </Box>
        ) : null}

        {actions ? (
          <Box
            className="section-card-actions"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              display: 'flex',
              gap: 0.5,
              opacity: 0,
              transition: 'opacity 0.12s',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {actions}
          </Box>
        ) : null}
      </Box>

      <Box sx={{ px: 1, py: 0.75 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT, minWidth: 0 }} noWrap>
            {section.name}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
            <Chip
              label={section.category}
              size="small"
              sx={{ height: 16, fontSize: 9, textTransform: 'capitalize' }}
            />
          </Box>
        </Box>
        {slotCount > 0 && (
          <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
            <SlotIcon sx={{ fontSize: 10, color: INDIGO }} />
            <Typography sx={{ fontSize: 10, color: TEXT_DIM }}>
              {slotCount} slot{slotCount === 1 ? '' : 's'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
